import { FC, useEffect, lazy } from 'react';
import { Fade, Paper, Box, Typography, Button } from '@mui/material';
import { useNavigate } from 'react-router';
import Home from '@mui/icons-material/Home';
import { useAppDispatch } from '@acorn-portal/shared/src/hooks/reduxHook';
import { clearRenewalState } from '@acorn-portal/shared/src/store/renewal.slice';
import { resetRenewalChanges } from '@acorn-portal/shared/src/store/renewalChanges.slice';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

interface BannerProps {
	text: React.ReactNode;
}

const NullBanner: FC<BannerProps> = () => null;
const nullModule = Promise.resolve({ default: NullBanner });

const companyName = import.meta.env.VITE_AUTH_REALM;

const LanderBanner = lazy<FC<BannerProps>>(() => {
	if (companyName === 'Motorcade') return import('@acorn-portal/shared/src/components/banner/MotorcadeLanderBanner');
	if (companyName === 'Acorn') return import('@acorn-portal/shared/src/components/banner/AcornLanderBanner');
	if (companyName === 'Flag') return import('@acorn-portal/shared/src/components/banner/FlagLanderBanner');
	if (companyName === 'MyPolicy') return import('@acorn-portal/shared/src/components/banner/MyPolicyLanderBanner');
	if (companyName === 'Carrot') return import('@acorn-portal/shared/src/components/banner/CarrotLanderBanner');
	return nullModule;
});

const Banner = ({ text }: BannerProps) => <LanderBanner text={text} />;

const RenewalReferralPage: FC = () => {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();

	const { track } = useTrack();

	useEffect(() => {
		track('Renewal Referral Page Viewed');
	}, []);

	useEffect(() => {
		return () => {
			dispatch(clearRenewalState());
			dispatch(resetRenewalChanges());
		};
	}, [dispatch]);

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<Fade in>
				<Box>
					<Banner
						text={
							<Box
								sx={(theme) => ({
									[theme.breakpoints.down('md')]: { fontSize: '1.3rem' },
								})}
							>
								Thank You, we’re reviewing your quote
							</Box>
						}
					/>
					<Box
						sx={{
							display: 'flex',
							justifyContent: 'center',
							flexDirection: 'column',
							alignItems: 'center',
							textAlign: 'center',
							px: 2,
							'& > *': { mb: 3 },
						}}
					>
						<Box
							sx={(theme) => ({
								mt: 4,
								mx: '10%',
								'& > *': { mb: 3 },
								[theme.breakpoints.down('md')]: {
									mx: 'unset',
								},
							})}
						>
							<Box>
								<Typography variant='h6' fontWeight='lighter'>
									Thank you for requesting a Renewal quote with {companyName} today. Unfortunately, we are unable to
									progress with the proposed change at this time.
								</Typography>
							</Box>
							<Typography
								variant='body1'
								sx={(theme) => ({
									m: 2,
									[theme.breakpoints.down('md')]: { m: 'unset', mt: 2 },
								})}
							>
								Please contact us on{' '}
								<Typography component='a' href='tel:01704339327' color='primary' fontWeight='bold'>
									01704 339327
								</Typography>{' '}
								or via the webchat option in the bottom right of your screen if you wish to discuss your renewal
								further.
							</Typography>
							<Typography>
								Thank you for your interest in {companyName} insurance - we look forward to speaking to you soon.
							</Typography>
						</Box>
						<Button startIcon={<Home />} size='large' variant='contained' onClick={() => navigate('/portal/home')}>
							Return to homepage
						</Button>
						<Box sx={{ margin: 2 }} />
					</Box>
				</Box>
			</Fade>
		</Paper>
	);
};

export default RenewalReferralPage;
